import { useState, type FormEvent } from 'react'
import { Modal } from '../../components/Modal'
import { ApiClientError } from '../../services/apiClient'
import {
  clientesClient,
  validateRfc,
  type Client,
  type ClientCreatePayload,
  type ClientUpdatePayload,
} from '../../services/clientesClient'

type Mode = 'create' | 'edit'

type Props = {
  open: boolean
  mode: Mode
  /** Si mode='edit', el cliente a editar. */
  client?: Client | null
  onClose: () => void
  onSaved: (client: Client) => void
}

/**
 * Modal de alta y edición de cliente (CLI-05 + CLI-06).
 *
 * Mismo componente para crear y editar — la diferencia es el endpoint llamado.
 * Validación de RFC en cliente (CLI-04) antes de enviar; backend re-valida.
 */
export function ClientFormModal({ open, mode, client, onClose, onSaved }: Props) {
  const [alias, setAlias] = useState(client?.alias ?? '')
  const [legalName, setLegalName] = useState(client?.legal_name ?? '')
  const [taxId, setTaxId] = useState(client?.tax_id ?? '')
  const [address, setAddress] = useState(client?.address ?? '')
  const [plant, setPlant] = useState(client?.plant ?? '')
  const [receptionContact, setReceptionContact] = useState(client?.reception_contact ?? '')
  const [email, setEmail] = useState(client?.email ?? '')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Re-sincronizar campos cuando cambia el cliente o el modo (sin useEffect: form key del padre).
  // El padre debe pasar `key={client?.id ?? 'new'}` al montar para resetear estado.

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setFieldErrors({})

    // Validación client-side de RFC (CLI-04).
    const rfcError = validateRfc(taxId)
    if (rfcError) {
      setFieldErrors({ tax_id: rfcError })
      return
    }

    setSubmitting(true)
    try {
      const payload: ClientCreatePayload = {
        alias: alias.trim(),
        legal_name: legalName.trim(),
        tax_id: taxId.trim(),
        address: address.trim() || undefined,
        plant: plant.trim() || undefined,
        reception_contact: receptionContact.trim() || undefined,
        email: email.trim() || undefined,
      }

      let result
      if (mode === 'create') {
        result = await clientesClient.create(payload)
      } else if (client) {
        const update: ClientUpdatePayload = {
          alias: payload.alias,
          legal_name: payload.legal_name,
          tax_id: payload.tax_id,
          address: payload.address ?? '',
          plant: payload.plant ?? '',
          reception_contact: payload.reception_contact ?? '',
          email: payload.email ?? '',
        }
        result = await clientesClient.update(client.id, update)
      } else {
        throw new Error('Modo edit sin cliente.')
      }
      onSaved(result.client)
    } catch (err) {
      if (err instanceof ApiClientError) {
        const fields = err.apiError.details?.fields
        if (fields && typeof fields === 'object') {
          setFieldErrors(fields as Record<string, string>)
        }
        setError(err.apiError.message)
      } else {
        setError('No se pudo guardar el cliente.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'create' ? 'Nuevo cliente' : `Editar ${client?.alias ?? 'cliente'}`}
      size="lg"
      footer={
        <div className="modal__actions">
          <button type="button" className="ghost-btn" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button
            type="submit"
            form="client-form"
            className="primary-btn"
            disabled={submitting}
          >
            {submitting ? 'Guardando…' : mode === 'create' ? 'Crear cliente' : 'Guardar cambios'}
          </button>
        </div>
      }
    >
      <form id="client-form" onSubmit={handleSubmit} className="form-grid" noValidate>
        <label className="field">
          <span>Alias <em className="muted">(nombre corto interno)</em></span>
          <input
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            required
            maxLength={255}
            disabled={submitting}
            autoFocus
          />
          {fieldErrors.alias && <small className="field-error">{fieldErrors.alias}</small>}
        </label>

        <label className="field">
          <span>Razón social</span>
          <input
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
            required
            maxLength={512}
            disabled={submitting}
          />
          {fieldErrors.legal_name && <small className="field-error">{fieldErrors.legal_name}</small>}
        </label>

        <label className="field">
          <span>RFC</span>
          <input
            value={taxId}
            onChange={(e) => setTaxId(e.target.value.toUpperCase())}
            required
            maxLength={20}
            placeholder="ABCD850101XYZ"
            disabled={submitting}
            className="mono"
          />
          {fieldErrors.tax_id && <small className="field-error">{fieldErrors.tax_id}</small>}
        </label>

        <label className="field">
          <span>Domicilio <em className="muted">(opcional)</em></span>
          <textarea
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            maxLength={1024}
            rows={2}
            disabled={submitting}
          />
          {fieldErrors.address && <small className="field-error">{fieldErrors.address}</small>}
        </label>

        <label className="field">
          <span>Planta <em className="muted">(opcional)</em></span>
          <input
            value={plant}
            onChange={(e) => setPlant(e.target.value)}
            maxLength={255}
            disabled={submitting}
          />
          {fieldErrors.plant && <small className="field-error">{fieldErrors.plant}</small>}
        </label>

        <label className="field">
          <span>Responsable de recepción <em className="muted">(opcional)</em></span>
          <input
            value={receptionContact}
            onChange={(e) => setReceptionContact(e.target.value)}
            maxLength={512}
            disabled={submitting}
          />
          {fieldErrors.reception_contact && (
            <small className="field-error">{fieldErrors.reception_contact}</small>
          )}
        </label>

        <label className="field">
          <span>Correo electrónico <em className="muted">(opcional — se usa para envío del PDF al cerrar)</em></span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={255}
            disabled={submitting}
            placeholder="contacto@empresa.com"
          />
          {fieldErrors.email && <small className="field-error">{fieldErrors.email}</small>}
        </label>

        {error && Object.keys(fieldErrors).length === 0 && <p className="error">{error}</p>}
      </form>
    </Modal>
  )
}
